import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useT } from "@/i18n";
import { LangToggle } from "@/components/LangToggle";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Download, CheckCircle2, Share, PlusSquare, MoreVertical, Monitor, Smartphone,
  Apple, Chrome, Home, Sparkles, Zap, WifiOff, ArrowLeft,
} from "lucide-react";

function detectOS() {
  const ua = navigator.userAgent || "";
  if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
  if (/Android/i.test(ua)) return "android";
  return "pc";
}

const Step = ({ n, icon: Icon, children }) => (
  <li className="flex gap-3" data-testid={`install-step-${n}`}>
    <span className="shrink-0 w-8 h-8 rounded-full bg-primary/15 border border-primary/40 text-primary font-bold flex items-center justify-center text-sm">
      {n}
    </span>
    <div className="pt-1 text-slate-300 text-sm leading-relaxed flex-1">{children}</div>
    {Icon && <Icon className="w-5 h-5 text-slate-500 mt-1.5 shrink-0" />}
  </li>
);

const Benefit = ({ icon: Icon, title, desc }) => (
  <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4">
    <Icon className="w-6 h-6 text-primary mb-2" />
    <p className="text-white font-medium text-sm">{title}</p>
    <p className="text-slate-400 text-xs mt-1">{desc}</p>
  </div>
);

export default function Install() {
  const { t } = useT();
  const [tab, setTab] = useState("pc");
  const [canInstall, setCanInstall] = useState(!!window.__pwaPrompt);
  const [installed, setInstalled] = useState(
    window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone === true
  );

  useEffect(() => {
    setTab(detectOS());
    const onReady = () => setCanInstall(true);
    const onInstalled = () => { setInstalled(true); setCanInstall(false); };
    window.addEventListener("pwaPromptReady", onReady);
    window.addEventListener("pwaInstalled", onInstalled);
    return () => {
      window.removeEventListener("pwaPromptReady", onReady);
      window.removeEventListener("pwaInstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    const p = window.__pwaPrompt;
    if (!p) return;
    p.prompt();
    const { outcome } = await p.userChoice;
    if (outcome === "accepted") setInstalled(true);
    window.__pwaPrompt = null;
    setCanInstall(false);
  };

  return (
    <div className="min-h-screen bg-[#0D0E11]">
      <header className="sticky top-0 z-50 glass border-b border-slate-800 h-16 flex items-center">
        <div className="w-full max-w-5xl mx-auto px-4 sm:px-6 flex items-center justify-between">
          <Link to="/" data-testid="install-home-link"><Logo className="h-7 sm:h-8" /></Link>
          <div className="flex items-center gap-3">
            <LangToggle />
            <Link to="/" className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-white transition-colors" data-testid="install-back-link">
              <ArrowLeft className="w-4 h-4" /> {t("Înapoi")}
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <div className="text-center animate-fade-up">
          <span className="inline-flex items-center gap-1.5 text-xs text-primary bg-primary/10 border border-primary/30 rounded-full px-3 py-1 mb-4">
            <Download className="w-3.5 h-3.5" /> {t("Instalare aplicație")}
          </span>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-white tracking-tight">
            {t("Instalează KLUS AI Studio")}
          </h1>
          <p className="text-slate-400 text-base mt-3 max-w-xl mx-auto">
            {t("Adaugă aplicația pe telefon sau computer pentru acces instant, pe tot ecranul, direct de pe ecranul principal — exact ca o aplicație nativă. Nu ocupă spațiu și nu trece prin App Store.")}
          </p>
        </div>

        {installed ? (
          <div className="mt-8 rounded-2xl border border-green-500/30 bg-green-500/5 p-5 flex items-center gap-3 justify-center" data-testid="install-already">
            <CheckCircle2 className="w-6 h-6 text-green-400" />
            <p className="text-white font-medium">{t("Aplicația este deja instalată. O poți deschide de pe ecranul principal.")}</p>
          </div>
        ) : canInstall ? (
          <div className="mt-8 text-center">
            <Button onClick={install} data-testid="install-now-btn" size="lg"
              className="bg-primary hover:bg-primary/90 text-white h-12 px-8 text-base">
              <Download className="w-5 h-5 mr-2" /> {t("Instalează acum")}
            </Button>
            <p className="text-slate-500 text-xs mt-2">{t("Cel mai simplu — browserul tău acceptă instalarea cu un clic.")}</p>
          </div>
        ) : null}

        <div className="grid grid-cols-3 gap-3 mt-10">
          <Benefit icon={Zap} title={t("Rapid")} desc={t("Se deschide instant, pe tot ecranul, fără bara browserului.")} />
          <Benefit icon={Home} title={t("Pe ecranul principal")} desc={t("Iconiță KLUS lângă celelalte aplicații.")} />
          <Benefit icon={WifiOff} title={t("Comod")} desc={t("Un singur tap pentru randări noi, oriunde te afli.")} />
        </div>

        <div className="mt-10">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid grid-cols-3 w-full bg-slate-900 border border-slate-800 h-auto p-1">
              <TabsTrigger value="pc" data-testid="install-tab-pc" className="data-[state=active]:bg-primary data-[state=active]:text-white py-2.5">
                <Monitor className="w-4 h-4 mr-2" /> PC
              </TabsTrigger>
              <TabsTrigger value="android" data-testid="install-tab-android" className="data-[state=active]:bg-primary data-[state=active]:text-white py-2.5">
                <Smartphone className="w-4 h-4 mr-2" /> Android
              </TabsTrigger>
              <TabsTrigger value="ios" data-testid="install-tab-ios" className="data-[state=active]:bg-primary data-[state=active]:text-white py-2.5">
                <Apple className="w-4 h-4 mr-2" /> iPhone / iPad
              </TabsTrigger>
            </TabsList>

            <TabsContent value="pc" className="mt-6">
              <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-6">
                <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
                  <Chrome className="w-5 h-5 text-primary" /> {t("Windows sau Mac (Chrome / Edge)")}
                </h2>
                <ol className="space-y-4">
                  <Step n={1}>{t("Deschide aplicația în")} <b className="text-white">Google Chrome</b> {t("sau")} <b className="text-white">Microsoft Edge</b>.</Step>
                  <Step n={2} icon={Download}>{t("În bara de adrese (dreapta), apasă pictograma de")} <b className="text-white">{t("instalare")}</b> {t("(un monitor cu o săgeată). Alternativ, meniul")} <MoreVertical className="inline w-4 h-4" /> → <b className="text-white">{t("„Instalează KLUS AI Studio…”")}</b>.</Step>
                  <Step n={3}>{t("Confirmă apăsând")} <b className="text-white">{t("„Instalează”")}</b>.</Step>
                  <Step n={4} icon={CheckCircle2}>{t("Aplicația apare în meniul Start / Launchpad și se deschide într-o fereastră proprie.")}</Step>
                </ol>
                <p className="text-slate-500 text-xs mt-5 border-t border-slate-800 pt-4">
                  {t("Sfat: dacă folosești butonul")} <b className="text-slate-300">{t("„Instalează acum”")}</b> {t("de mai sus, sari peste toți pașii.")}
                </p>
              </div>
            </TabsContent>

            <TabsContent value="android" className="mt-6">
              <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-6">
                <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
                  <Chrome className="w-5 h-5 text-primary" /> {t("Android (Chrome)")}
                </h2>
                <ol className="space-y-4">
                  <Step n={1}>{t("Deschide aplicația în")} <b className="text-white">Google Chrome</b>.</Step>
                  <Step n={2} icon={MoreVertical}>{t("Apasă meniul cu")} <b className="text-white">{t("trei puncte")}</b> {t("(dreapta sus).")}</Step>
                  <Step n={3} icon={PlusSquare}>{t("Alege")} <b className="text-white">{t("„Instalează aplicația”")}</b> {t("sau")} <b className="text-white">{t("„Adaugă pe ecranul principal”")}</b>.</Step>
                  <Step n={4} icon={CheckCircle2}>{t("Confirmă cu")} <b className="text-white">{t("„Instalează”")}</b>. {t("Iconița KLUS apare pe ecranul principal.")}</Step>
                </ol>
                <p className="text-slate-500 text-xs mt-5 border-t border-slate-800 pt-4">
                  {t("Dacă apare automat bannerul")} <b className="text-slate-300">{t("„Instalează”")}</b> {t("jos, apasă-l direct.")}
                </p>
              </div>
            </TabsContent>

            <TabsContent value="ios" className="mt-6">
              <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-6">
                <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
                  <Apple className="w-5 h-5 text-primary" /> {t("iPhone / iPad (Safari)")}
                </h2>
                <ol className="space-y-4">
                  <Step n={1}>{t("Deschide aplicația neapărat în")} <b className="text-white">Safari</b> {t("(nu în Chrome pe iOS).")}</Step>
                  <Step n={2} icon={Share}>{t("Apasă butonul")} <b className="text-white">{t("Partajare")}</b> {t("(pătrat cu săgeată în sus), în bara de jos.")}</Step>
                  <Step n={3} icon={PlusSquare}>{t("Derulează și alege")} <b className="text-white">{t("„Adaugă la ecranul principal”")}</b> (Add to Home Screen).</Step>
                  <Step n={4} icon={CheckCircle2}>{t("Apasă")} <b className="text-white">{t("„Adaugă”")}</b> {t("(dreapta sus). Iconița KLUS apare pe ecranul principal.")}</Step>
                </ol>
                <p className="text-slate-500 text-xs mt-5 border-t border-slate-800 pt-4">
                  {t("Notă: pe iPhone/iPad, instalarea se face doar din")} <b className="text-slate-300">Safari</b> {t("— butonul automat de instalare nu este disponibil (limitare Apple).")}
                </p>
              </div>
            </TabsContent>
          </Tabs>
        </div>

        <div className="mt-10 text-center">
          <Link to="/studio">
            <Button variant="outline" data-testid="install-open-app-btn" className="border-slate-700 text-white hover:bg-slate-800">
              <Sparkles className="w-4 h-4 mr-2" /> {t("Deschide aplicația")}
            </Button>
          </Link>
        </div>
      </main>
    </div>
  );
}
